import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { apiService, AdminFeature, PlanFeatureListItem } from '../../services/api';

interface PlanFeaturesDialogProps {
  planId: string | null;
  planName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog mit Checkbox-Liste aller Features für den gewählten
 * SubscriptionPlan (CA-380). UX mirrors the PlanToolsDialog — auswählen
 * was gelten soll, "Speichern" diff-applies (add/remove).
 */
export function PlanFeaturesDialog({
  planId,
  planName,
  open,
  onOpenChange,
}: PlanFeaturesDialogProps) {
  const [allFeatures, setAllFeatures] = useState<AdminFeature[]>([]);
  const [initialSelection, setInitialSelection] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !planId) {
      setAllFeatures([]);
      setInitialSelection(new Set());
      setSelected(new Set());
      setSearch('');
      return;
    }
    setLoading(true);
    void Promise.all([apiService.listFeatures(), apiService.listPlanFeatures(planId)])
      .then(([all, planFeatures]) => {
        setAllFeatures(all);
        const ids = new Set(
          planFeatures
            .filter((pf: PlanFeatureListItem) => pf.isEnabled)
            .map((pf: PlanFeatureListItem) => pf.featureId),
        );
        setInitialSelection(ids);
        setSelected(new Set(ids));
      })
      .catch(err => {
        toast.error('Features konnten nicht geladen werden.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [open, planId]);

  const filteredFeatures = useMemo(() => {
    const activeFeatures = allFeatures.filter(f => f.isActive);
    const q = search.trim().toLowerCase();
    if (!q) return activeFeatures;
    return activeFeatures.filter(
      f =>
        f.displayName.toLowerCase().includes(q) ||
        f.name.toLowerCase().includes(q),
    );
  }, [allFeatures, search]);

  const totalActiveFeatures = useMemo(
    () => allFeatures.filter(f => f.isActive).length,
    [allFeatures],
  );

  const toggle = (featureId: string, checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(featureId);
      else next.delete(featureId);
      return next;
    });
  };

  const hasChanges = useMemo(() => {
    if (selected.size !== initialSelection.size) return true;
    for (const id of selected) {
      if (!initialSelection.has(id)) return true;
    }
    return false;
  }, [selected, initialSelection]);

  const handleSave = async () => {
    if (!planId || !hasChanges) return;

    const toAdd: string[] = [];
    const toRemove: string[] = [];
    for (const id of selected) if (!initialSelection.has(id)) toAdd.push(id);
    for (const id of initialSelection) if (!selected.has(id)) toRemove.push(id);

    setSaving(true);
    try {
      await Promise.all([
        ...toAdd.map(id => apiService.assignPlanFeature(planId, { featureId: id })),
        ...toRemove.map(id => apiService.removePlanFeature(planId, id)),
      ]);
      toast.success(
        `Features gespeichert (${toAdd.length} hinzugefügt, ${toRemove.length} entfernt).`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error('Speichern fehlgeschlagen.');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Features für „{planName}" verwalten</DialogTitle>
          <DialogDescription>
            Wähle alle Features aus, die im Plan freigeschaltet sein sollen. Customers mit
            diesem Plan sehen diese Features im Portal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            placeholder="Feature suchen…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            disabled={loading}
          />

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredFeatures.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Keine Features gefunden.
            </p>
          ) : (
            <div className="max-h-[420px] space-y-1.5 overflow-y-auto rounded-lg border p-3">
              {filteredFeatures.map(feature => (
                <div key={feature.id} className="flex items-start space-x-2">
                  <Checkbox
                    id={`plan-feature-${feature.id}`}
                    checked={selected.has(feature.id)}
                    onCheckedChange={c => toggle(feature.id, c === true)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={`plan-feature-${feature.id}`}
                    className="cursor-pointer text-sm font-normal leading-tight"
                  >
                    <div>{feature.displayName}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {feature.name}
                    </div>
                    {feature.description && (
                      <div className="text-xs text-muted-foreground">
                        {feature.description}
                      </div>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground">
            {selected.size} von {totalActiveFeatures} ausgewählt
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving || loading}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Speichern…
              </>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
